import { Search, User, LogOut, Download, Menu, Home, RefreshCw, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate, Link } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationCenter } from "@/components/Notifications";
import { NFCConnectionIndicator } from "@/components/NFC/NFCConnectionIndicator";
import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

interface HeaderProps {
  onMenuClick?: () => void;
}

export const Header = ({ onMenuClick }: HeaderProps) => {
  const { user, profile, userRole, signOut } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries();
      await queryClient.refetchQueries({ type: 'active' });
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  }, [queryClient]);

  useEffect(() => {
    // Mostrar botón solo si no está instalada
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    setShowInstallButton(!isStandalone);
  }, []);

  const getRoleBadgeVariant = (role: string | null) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'supervisor':
        return 'default';
      case 'viewer':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getRoleLabel = (role: string | null) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'supervisor':
        return 'Supervisor';
      case 'operator':
        return 'Operador';
      case 'viewer':
        return 'Invitado';
      case 'quality':
        return 'Calidad';
      case 'warehouse':
        return 'Almacén';
      case 'maintenance':
        return 'Mantenimiento';
      default:
        return 'Usuario';
    }
  };

  const handleSearch = () => {
    const query = searchQuery.trim();
    if (!query) return;
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <header className="h-12 sm:h-14 md:h-16 border-b border-border bg-card px-2 sm:px-4 md:px-6 flex items-center justify-between sticky top-0 z-40 safe-area-top">
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="lg:hidden flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10"
        >
          <Menu className="w-5 h-5" />
        </Button>

        {/* Home Button - Always visible */}
        <Link to="/">
          <Button
            variant="ghost"
            size="icon"
            title="Ir al inicio"
            className="flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10"
          >
            <Home className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        </Link>
      </div>

      {/* Search - Hidden on mobile */}
      <div className="hidden md:flex items-center gap-4 flex-1 max-w-xl mx-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            className="pl-10 bg-background h-9"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSearch();
              }
            }}
          />
        </div>
      </div>
      
      <div className="flex items-center gap-1 sm:gap-2 md:gap-3 ml-auto">
        {showInstallButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/install')}
            title="Instalar App"
            className="hidden sm:flex h-9 w-9 sm:h-10 sm:w-10"
          >
            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Actualizar datos"
          className="h-9 w-9 sm:h-10 sm:w-10"
        >
          <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        <NFCConnectionIndicator />

        {/* Language Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" title="Idioma / Language" className="h-9 w-9 sm:h-10 sm:w-10">
              <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => setLanguage("es")}
              className={language === "es" ? "font-bold bg-accent" : ""}
            >
              <span className="mr-2">🇪🇸</span> Español
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setLanguage("en")}
              className={language === "en" ? "font-bold bg-accent" : ""}
            >
              <span className="mr-2">🇬🇧</span> English
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ThemeToggle />
        <NotificationCenter />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 sm:w-56 mr-1 sm:mr-2">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-xs sm:text-sm font-medium truncate">{profile?.full_name || 'Usuario'}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{user?.email}</p>
                {profile?.employee_id && (
                  <p className="text-[10px] sm:text-xs text-muted-foreground">{profile.employee_id}</p>
                )}
                <Badge variant={getRoleBadgeVariant(userRole)} className="w-fit mt-1 text-[10px] sm:text-xs">
                  {getRoleLabel(userRole)}
                </Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/my-profile')} className="text-xs sm:text-sm">
              <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
              Mi Perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive text-xs sm:text-sm">
              <LogOut className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
